import parseSearchPrefix from '~/components/search/parseSearchPrefix'

import type { SearchCategory } from '../types'

type ParsedFilter = {
  /** The category to filter on, or null if no filter prefix was detected */
  category: SearchCategory | null
  /** The search term after removing the filter prefix */
  searchTerm: string
}

/**
 * Maps filter prefixes to their corresponding search categories.
 * Includes both full names and single-character shortcuts.
 *
 * Examples:
 *   "charger:ev100"  → { category: 'charger', searchTerm: 'ev100' }
 *   "c:ev100"        → { category: 'charger', searchTerm: 'ev100' }
 *   "site:depot"     → { category: 'site', searchTerm: 'depot' }
 *   "s:depot"        → { category: 'site', searchTerm: 'depot' }
 *   "hello world"    → { category: null, searchTerm: 'hello world' }
 */
const FILTER_PREFIX_MAP: Record<string, SearchCategory> = {
  // Full names
  charger: 'charger',
  site: 'site',
  vehicle: 'vehicle',
  user: 'user',
  org: 'org',
  synoplink: 'synoplink',
  solar: 'solarArray',
  page: 'page',

  // Shortcuts
  c: 'charger',
  s: 'site',
  v: 'vehicle',
  u: 'user',
  o: 'org',
  sl: 'synoplink',
  sa: 'solarArray',
  p: 'page'
}

/**
 * Parses a search query for a category filter prefix.
 *
 * Supported formats:
 *   - Full name: "charger:", "site:", "vehicle:", "user:", "org:", "synoplink:", "page:"
 *   - Shortcuts: "c:", "s:", "v:", "u:", "o:", "sl:", "p:"
 *
 * The prefix is case-insensitive. The search term is everything after the colon,
 * with leading/trailing whitespace trimmed.
 */
export default function parseSearchFilter(query: string): ParsedFilter {
  return parseSearchPrefix(query, FILTER_PREFIX_MAP)
}
